import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  History,
  Link2,
  AlertTriangle,
} from "lucide-react";

type Source = { label: string; ref: string };

interface CanonEntry {
  id: string;
  canon_id: string;
  section: string;
  display_order: number;
  statement: string;
  boundaries: string | null;
  sources: Source[];
  status: string;
  version: number;
  content_hash: string | null;
  change_note: string | null;
  ratified_by: string | null;
  ratified_at: string | null;
  pending_statement: string | null;
  pending_boundaries: string | null;
  pending_sources: Source[] | null;
  pending_change_note: string | null;
  pending_staged_by: string | null;
  pending_staged_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ArtifactLink {
  id: string;
  canon_entry_id: string;
  canon_version: number;
  artifact_type: string;
  artifact_ref: string;
  anchor_text: string | null;
  link_type: string;
  is_stale: boolean;
  verified_at: string | null;
  created_at: string;
}

interface CanonRevision {
  id: string;
  canon_entry_id: string;
  canon_id: string;
  section: string | null;
  statement: string | null;
  boundaries: string | null;
  version: number | null;
  status: string | null;
  change_note: string | null;
  archived_at: string;
}

const ARTIFACT_TYPES = ["document", "skill", "prompt", "portal"];
const LINK_TYPES = ["states", "applies", "contradicts", "tension"];

const emptyDraft = {
  canon_id: "",
  section: "",
  display_order: 0,
  statement: "",
  boundaries: "",
  sources: [] as Source[],
};

function toSources(value: unknown): Source[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((s) => s && typeof s === "object")
    .map((s) => ({
      label: String((s as any).label ?? ""),
      ref: String((s as any).ref ?? ""),
    }));
}

export default function CanonRegister() {
  const { toast } = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<CanonEntry[]>([]);
  const [links, setLinks] = useState<ArtifactLink[]>([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ ...emptyDraft });

  // ratify (first ratification)
  const [ratifyTarget, setRatifyTarget] = useState<CanonEntry | null>(null);

  // seal (ratify a pending amendment)
  const [sealTarget, setSealTarget] = useState<CanonEntry | null>(null);

  // discard pending
  const [discardTarget, setDiscardTarget] = useState<CanonEntry | null>(null);

  // amendment
  const [amendTarget, setAmendTarget] = useState<CanonEntry | null>(null);
  const [amendStatement, setAmendStatement] = useState("");
  const [amendNote, setAmendNote] = useState("");
  const [amendResult, setAmendResult] = useState<string | null>(null);

  // detail
  const [detailTarget, setDetailTarget] = useState<CanonEntry | null>(null);
  const [revisions, setRevisions] = useState<CanonRevision[]>([]);
  const [newLink, setNewLink] = useState({
    artifact_type: "document",
    artifact_ref: "",
    anchor_text: "",
    link_type: "states",
  });

  async function load() {
    setLoading(true);
    try {
      const [entryRes, linkRes] = await Promise.all([
        supabase
          .from("canon_entries")
          .select("*")
          .order("section", { ascending: true })
          .order("display_order", { ascending: true }),
        supabase.from("artifact_links").select("*"),
      ]);
      if (entryRes.error) throw entryRes.error;
      if (linkRes.error) throw linkRes.error;
      setEntries(
        (entryRes.data || []).map((e: any) => ({
          ...e,
          sources: toSources(e.sources),
          pending_sources: e.pending_sources ? toSources(e.pending_sources) : null,
        })) as CanonEntry[],
      );
      setLinks((linkRes.data || []) as ArtifactLink[]);
    } catch (err: any) {
      toast({
        title: "Could not load the canon register",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => {
      const haystack = [
        e.canon_id,
        e.statement,
        e.boundaries || "",
        e.sources.map((s) => `${s.label} ${s.ref}`).join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [entries, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, CanonEntry[]>();
    for (const e of filtered) {
      const list = map.get(e.section) || [];
      list.push(e);
      map.set(e.section, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.display_order - b.display_order);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const dependents = (entryId: string) =>
    links.filter((l) => l.canon_entry_id === entryId);

  function openCreate() {
    setEditingId(null);
    setDraft({ ...emptyDraft });
    setEditorOpen(true);
  }

  function openEdit(entry: CanonEntry) {
    setEditingId(entry.id);
    setDraft({
      canon_id: entry.canon_id,
      section: entry.section,
      display_order: entry.display_order,
      statement: entry.statement,
      boundaries: entry.boundaries || "",
      sources: entry.sources,
    });
    setEditorOpen(true);
  }

  async function saveDraft() {
    if (!draft.canon_id.trim() || !draft.section.trim() || !draft.statement.trim()) {
      toast({
        title: "Missing fields",
        description: "Canon id, section and statement are required.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        canon_id: draft.canon_id.trim(),
        section: draft.section.trim(),
        display_order: Number(draft.display_order) || 0,
        statement: draft.statement.trim(),
        boundaries: draft.boundaries.trim() || null,
        sources: draft.sources.filter((s) => s.label.trim() || s.ref.trim()) as any,
      };
      const { error } = editingId
        ? await supabase.from("canon_entries").update(payload).eq("id", editingId)
        : await supabase.from("canon_entries").insert(payload);
      if (error) throw error;
      toast({ title: editingId ? "Entry updated" : "Entry created" });
      setEditorOpen(false);
      await load();
    } catch (err: any) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function ratify(entry: CanonEntry) {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("canon_entries")
        .update({
          status: "ratified",
          ratified_by: user?.id ?? null,
          ratified_at: new Date().toISOString(),
        })
        .eq("id", entry.id);
      if (error) throw error;
      toast({ title: `${entry.canon_id} ratified` });
      setRatifyTarget(null);
      await load();
    } catch (err: any) {
      toast({ title: "Ratify failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  function openAmend(entry: CanonEntry) {
    setAmendTarget(entry);
    setAmendStatement(entry.pending_statement ?? entry.statement);
    setAmendNote(entry.pending_change_note ?? "");
    setAmendResult(null);
  }

  async function saveAmendment() {
    if (!amendTarget) return;
    if (!amendStatement.trim() || !amendNote.trim()) {
      toast({
        title: "Missing fields",
        description: "A new statement and a change note are both required.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from("canon_entries")
        .update({
          pending_statement: amendStatement.trim(),
          pending_change_note: amendNote.trim(),
          pending_staged_by: user?.id ?? null,
          pending_staged_at: new Date().toISOString(),
        })
        .eq("id", amendTarget.id);
      if (error) throw error;
      setAmendResult(
        "Amendment staged for ratification. The current statement is still law until you press Ratify.",
      );
      await load();
    } catch (err: any) {
      toast({ title: "Amendment failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function sealAmendment(entry: CanonEntry) {
    if (!entry.pending_statement || !entry.pending_change_note) return;
    setSaving(true);
    try {
      const affected = dependents(entry.id).length;
      const { error } = await supabase
        .from("canon_entries")
        .update({
          statement: entry.pending_statement,
          boundaries: entry.pending_boundaries ?? entry.boundaries,
          sources: (entry.pending_sources ?? entry.sources) as any,
          change_note: entry.pending_change_note,
          ratified_by: user?.id ?? null,
          ratified_at: new Date().toISOString(),
          pending_statement: null,
          pending_boundaries: null,
          pending_sources: null,
          pending_change_note: null,
          pending_staged_by: null,
          pending_staged_at: null,
        })
        .eq("id", entry.id);
      if (error) throw error;
      toast({
        title: `${entry.canon_id} sealed`,
        description: `New statement is law. ${affected} dependent link${
          affected === 1 ? "" : "s"
        } marked stale.`,
      });
      setSealTarget(null);
      await load();
    } catch (err: any) {
      toast({ title: "Seal failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function discardPending(entry: CanonEntry) {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("canon_entries")
        .update({
          pending_statement: null,
          pending_boundaries: null,
          pending_sources: null,
          pending_change_note: null,
          pending_staged_by: null,
          pending_staged_at: null,
        })
        .eq("id", entry.id);
      if (error) throw error;
      toast({ title: "Pending amendment discarded" });
      setDiscardTarget(null);
      await load();
    } catch (err: any) {
      toast({ title: "Discard failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function openDetail(entry: CanonEntry) {
    setDetailTarget(entry);
    setRevisions([]);
    const { data, error } = await supabase
      .from("canon_revisions")
      .select("*")
      .eq("canon_entry_id", entry.id)
      .order("archived_at", { ascending: false });
    if (error) {
      toast({ title: "Could not load revisions", description: error.message, variant: "destructive" });
      return;
    }
    setRevisions((data || []) as CanonRevision[]);
  }

  async function addLink() {
    if (!detailTarget || !newLink.artifact_ref.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("artifact_links").insert({
        canon_entry_id: detailTarget.id,
        canon_version: detailTarget.version,
        artifact_type: newLink.artifact_type,
        artifact_ref: newLink.artifact_ref.trim(),
        anchor_text: newLink.anchor_text.trim() || null,
        link_type: newLink.link_type,
        verified_at: new Date().toISOString(),
      });
      if (error) throw error;
      setNewLink({ artifact_type: "document", artifact_ref: "", anchor_text: "", link_type: "states" });
      await load();
    } catch (err: any) {
      toast({ title: "Could not add link", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function removeLink(id: string) {
    setSaving(true);
    try {
      const { error } = await supabase.from("artifact_links").delete().eq("id", id);
      if (error) throw error;
      await load();
    } catch (err: any) {
      toast({ title: "Could not remove link", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const detailLinks = detailTarget ? dependents(detailTarget.id) : [];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Canon Register
          </h1>
          <p className="text-sm text-muted-foreground">
            Ratify and amend canonical doctrine, one entry at a time.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Search canon id, statement, boundaries, sources"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading register...
        </div>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {entries.length === 0
              ? "The register is empty. Create the first entry."
              : "No entries match this search."}
          </CardContent>
        </Card>
      ) : (
        grouped.map(([section, sectionEntries]) => (
          <div key={section} className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <Link
                to={`/admin/canon/${encodeURIComponent(section)}`}
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                {section}
                <ChevronRight className="h-4 w-4" />
              </Link>
            </h2>
            {sectionEntries.map((entry) => {
              const deps = dependents(entry.id);
              const staleCount = deps.filter((d) => d.is_stale).length;
              const hasPending = !!entry.pending_statement;
              return (
                <Card key={entry.id}>
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-base font-mono">{entry.canon_id}</CardTitle>
                      <Badge variant={entry.status === "ratified" ? "default" : "secondary"}>
                        {entry.status}
                      </Badge>
                      <Badge variant="outline">v{entry.version}</Badge>
                      <Badge variant="outline">
                        {deps.length} dependent{deps.length === 1 ? "" : "s"}
                      </Badge>
                      {staleCount > 0 && (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {staleCount} stale
                        </Badge>
                      )}
                      {hasPending && (
                        <Badge className="gap-1 bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20">
                          <AlertTriangle className="h-3 w-3" />
                          amendment pending
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-lg leading-relaxed">{entry.statement}</p>

                    {hasPending && (
                      <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                          Pending
                        </p>
                        <p className="whitespace-pre-wrap">{entry.pending_statement}</p>
                        {entry.pending_change_note && (
                          <p className="text-xs text-muted-foreground">
                            Change note: {entry.pending_change_note}
                          </p>
                        )}
                      </div>
                    )}


                    <Collapsible>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="gap-1 px-0">
                          <ChevronDown className="h-4 w-4" />
                          Boundaries and sources
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-2 space-y-3 text-sm">
                        <div>
                          <p className="font-medium">Boundaries</p>
                          <p className="text-muted-foreground whitespace-pre-wrap">
                            {entry.boundaries || "None recorded."}
                          </p>
                        </div>
                        <div>
                          <p className="font-medium">Sources</p>
                          {entry.sources.length === 0 ? (
                            <p className="text-muted-foreground">None recorded.</p>
                          ) : (
                            <ul className="list-disc pl-5 text-muted-foreground">
                              {entry.sources.map((s, i) => (
                                <li key={i}>
                                  {s.label}
                                  {s.ref ? `: ${s.ref}` : ""}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    <Separator />

                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => openDetail(entry)}>
                        <Link2 className="h-4 w-4 mr-2" />
                        Dependents and history
                      </Button>
                      {entry.status === "proposed" ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => openEdit(entry)}>
                            Edit
                          </Button>
                          <Button size="sm" onClick={() => setRatifyTarget(entry)}>
                            Ratify
                          </Button>
                        </>
                      ) : hasPending ? (
                        <>
                          <Button size="sm" onClick={() => setSealTarget(entry)}>
                            Ratify amendment v{entry.version + 1}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openAmend(entry)}>
                            Edit pending amendment
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDiscardTarget(entry)}
                          >
                            Discard pending
                          </Button>
                        </>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => openAmend(entry)}>
                          Propose Amendment
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))
      )}

      {/* Editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit proposed entry" : "New canon entry"}</DialogTitle>
            <DialogDescription>
              Proposed entries are freely editable. Once ratified they change only by amendment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Canon id</Label>
                <Input
                  value={draft.canon_id}
                  placeholder="CANON-001"
                  onChange={(e) => setDraft({ ...draft, canon_id: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Section</Label>
                <Input
                  value={draft.section}
                  onChange={(e) => setDraft({ ...draft, section: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Display order</Label>
                <Input
                  type="number"
                  value={draft.display_order}
                  onChange={(e) =>
                    setDraft({ ...draft, display_order: Number(e.target.value) || 0 })
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Statement</Label>
              <Textarea
                rows={4}
                value={draft.statement}
                onChange={(e) => setDraft({ ...draft, statement: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Boundaries</Label>
              <Textarea
                rows={3}
                value={draft.boundaries}
                onChange={(e) => setDraft({ ...draft, boundaries: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Sources</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setDraft({ ...draft, sources: [...draft.sources, { label: "", ref: "" }] })
                  }
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add source
                </Button>
              </div>
              {draft.sources.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    placeholder="Label"
                    value={s.label}
                    onChange={(e) => {
                      const next = [...draft.sources];
                      next[i] = { ...next[i], label: e.target.value };
                      setDraft({ ...draft, sources: next });
                    }}
                  />
                  <Input
                    placeholder="Ref"
                    value={s.ref}
                    onChange={(e) => {
                      const next = [...draft.sources];
                      next[i] = { ...next[i], ref: e.target.value };
                      setDraft({ ...draft, sources: next });
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setDraft({ ...draft, sources: draft.sources.filter((_, j) => j !== i) })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveDraft} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ratify confirm */}
      <AlertDialog open={!!ratifyTarget} onOpenChange={(o) => !o && setRatifyTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ratify {ratifyTarget?.canon_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              After ratification this entry can only change through an amendment with a change
              note. Each amendment archives the previous version and marks dependent links stale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (ratifyTarget) ratify(ratifyTarget);
              }}
            >
              Ratify
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Amendment */}
      <Dialog open={!!amendTarget} onOpenChange={(o) => !o && setAmendTarget(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Propose amendment to {amendTarget?.canon_id}</DialogTitle>
            <DialogDescription>
              The current statement is read only and stays law. Saving stages the new wording for
              seal: press Ratify on the entry to make it law.
            </DialogDescription>
          </DialogHeader>
          {amendResult ? (
            <div className="rounded-md border border-primary/40 bg-primary/5 p-4 text-sm">
              {amendResult}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Current statement (v{amendTarget?.version})</Label>
                  <Textarea rows={6} readOnly value={amendTarget?.statement || ""} />
                </div>
                <div className="space-y-1">
                  <Label>Pending statement</Label>
                  <Textarea
                    rows={6}
                    value={amendStatement}
                    onChange={(e) => setAmendStatement(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Change note (required)</Label>
                <Textarea
                  rows={3}
                  value={amendNote}
                  onChange={(e) => setAmendNote(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAmendTarget(null)}>
              {amendResult ? "Close" : "Cancel"}
            </Button>
            {!amendResult && (
              <Button onClick={saveAmendment} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Stage for ratification
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seal confirm */}
      <AlertDialog open={!!sealTarget} onOpenChange={(o) => !o && setSealTarget(null)}>
        <AlertDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Ratify {sealTarget?.canon_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sealing makes the new statement law: the current version archives, the version number
              rises, and dependent links go stale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-md border p-3">
                <p className="font-medium">Current (v{sealTarget?.version})</p>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {sealTarget?.statement}
                </p>
              </div>
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                <p className="font-medium">Pending</p>
                <p className="whitespace-pre-wrap">{sealTarget?.pending_statement}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Change note: {sealTarget?.pending_change_note}
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (sealTarget) sealAmendment(sealTarget);
              }}
            >
              Ratify
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discard pending confirm */}
      <AlertDialog open={!!discardTarget} onOpenChange={(o) => !o && setDiscardTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Discard the pending amendment to {discardTarget?.canon_id}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The pending wording and its change note are cleared. The current statement stays law
              and nothing is archived.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (discardTarget) discardPending(discardTarget);
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Detail: dependents and revisions */}
      <Dialog open={!!detailTarget} onOpenChange={(o) => !o && setDetailTarget(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailTarget?.canon_id}</DialogTitle>
            <DialogDescription>Dependents and revision history.</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Link2 className="h-4 w-4" /> Dependents ({detailLinks.length})
              </h3>
              {detailLinks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No links recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {detailLinks.map((l) => (
                    <div
                      key={l.id}
                      className="flex items-start justify-between gap-3 rounded-md border p-3"
                    >
                      <div className="space-y-1 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline">{l.artifact_type}</Badge>
                          <Badge variant="secondary">{l.link_type}</Badge>
                          <span className="font-mono">{l.artifact_ref}</span>
                          {l.is_stale && (
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" /> stale
                            </Badge>
                          )}
                        </div>
                        {l.anchor_text && (
                          <p className="text-muted-foreground">{l.anchor_text}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          canon v{l.canon_version} · verified{" "}
                          {l.verified_at ? new Date(l.verified_at).toLocaleString() : "never"}
                        </p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeLink(l.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="rounded-md border p-3 space-y-3">
                <p className="text-sm font-medium">Add link</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Select
                    value={newLink.artifact_type}
                    onValueChange={(v) => setNewLink({ ...newLink, artifact_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ARTIFACT_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={newLink.link_type}
                    onValueChange={(v) => setNewLink({ ...newLink, link_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINK_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Artifact ref (doc id, skill, prompt path, route)"
                    value={newLink.artifact_ref}
                    onChange={(e) => setNewLink({ ...newLink, artifact_ref: e.target.value })}
                  />
                  <Input
                    placeholder="Anchor text (optional)"
                    value={newLink.anchor_text}
                    onChange={(e) => setNewLink({ ...newLink, anchor_text: e.target.value })}
                  />
                </div>
                <Button size="sm" onClick={addLink} disabled={saving || !newLink.artifact_ref.trim()}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <History className="h-4 w-4" /> Revisions ({revisions.length})
              </h3>
              {revisions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No archived versions yet.</p>
              ) : (
                revisions.map((r) => (
                  <div key={r.id} className="rounded-md border p-3 space-y-1 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">v{r.version}</Badge>
                      <span className="text-xs text-muted-foreground">
                        archived {new Date(r.archived_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap">{r.statement}</p>
                    {r.change_note && (
                      <p className="text-xs text-muted-foreground">Note: {r.change_note}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
